<template>
  <div
    ref="rootRef"
    class="main"
    id="main"
    @click.capture="restartHumanLeftTimer"
  >
    <div
      :style="contentStyles"
      :class="{ 'hide-on-portrait': isIndependentVerticalBg }"
      class="horizontal-bg"
    />
    <div
      :style="verticalStyles"
      class="vertical-bg"
    />
    <video
      ref="videoRef"
      class="webcam"
      autoplay
      playsinline
      crossorigin="anonymous"
    />
    <video
      v-if="isVideoMedia && isHorizontalVideoBg"
      :key="currentContent.id"
      :class="{ 'hide-on-portrait': isIndependentVerticalBg }"
      id="video-background"
      ref="videoBgRef"
      autoplay
      playsinline
      crossorigin="anonymous"
      @ended="videoEndedHandler"
    >
      <source
        :src="mediaUrl"
        type="video/mp4"
      >
    </video>
    <video
      v-if="isVVideoMedia && !isHorizontalVideoBg"
      :key="currentContent.id"
      ref="vVideoBgRef"
      id="video-v-background"
      autoplay
      playsinline
      crossorigin="anonymous"
      @ended="vVideoEndedHandler"
    >
      <source
        :src="vMediaUrl"
        type="video/mp4"
      >
    </video>
    <div
      v-if="isProcessingAction && isOnline"
      :style="{ backgroundColor: overlayColor }"
      :class="{ 'hide-on-portrait': isIndependentVerticalBg }"
      class="processing-action-overlay"
    />
    <avatar
      v-show="(!isHideAvatar || isKeepAvatarDesign) && isOnline"
      :is-processing-action="isProcessingAction || isComponentProcessing"
      @progress="onAvatarLoadProgress"
      @ready="onAvatarReady"
    />
    <app-header
      v-if="isOnline"
      @show-menu="() => toggleLanguageMenu(true)"
      @restart-flow="restartFlow"
      @select-language="updateLanguage"
      @navigate-button="handleNavigateButton"
    />
    <loading-state
      v-show="((!ready && !error) || isProcessingAction ||
        isComponentProcessing || (loadingProjects.length > 0 && !showStartOverlay)) && isOnline"
      :text="!ready ? `Loading ${avatarLoadingPercentage}%` : 'Processing...'"
      :class="{
        ready,
        'has-avatar': !isHideAvatar || isKeepAvatarDesign,
        'central-design': useCenterVerticalDesktopDesign || (isHideAvatar && !isKeepAvatarDesign),
        'bottom-navigation-enabled': currentConfig.isBottomNavigationEnabled,
      }"
      :color="processingColor"
      class="loading-state"
    />
    <offline-overlay
      v-if="!isOnline"
    />
    <flow-page
      v-if="ready && !isProcessingAction && isOnline"
      @processing="isComponentProcessing = $event"
    />
    <app-footer
      v-if="ready && !isProcessingAction && isOnline && currentConfig.isBottomNavigationEnabled"
      @show-menu="() => toggleLanguageMenu(true)"
      @restart-flow="runFlow"
      @select-language="updateLanguage"
      @navigate-button="handleNavigateButton"
    />
    <menu-page
      v-if="languageMenuVisible && isOnline"
      @close="() => toggleLanguageMenu(false)"
      @restart-flow="restartFlow"
      @select-language="updateLanguage"
    />
    <start-overlay
      v-if="ready && showStartOverlay && !disableOverlay && isOnline"
      :can-play="isSetupComplete"
      @close="closeStartOverlay"
    />
    <div
      v-if="useOverlay && isOnline"
      ref="previewOverlayRef"
      class="preview-overlay"
    />
    <error-component
      v-if="error && isOnline"
      :error="error"
    />
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  watch,
  ref,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import {
  api, ShortCodeService, route, useAvatarState, useStt, useTts,
} from '@ht/lib-plural-vue';
import useConfig from '@/composables/useConfig';
import useFlows from '@/composables/useFlows';
import useContent from '@/composables/useContent';
import useSequence from '@/composables/useSequence';
import useActions from '@/composables/useActions';
import useMqtt from '@/composables/useMqtt';
import useFaceDetection from '@/composables/useFaceDetection';
import useRobotCommand from '@/composables/useRobotCommand';
import useAnalytics from '@/composables/useAnalytics';
import useR2RChat from '@/composables/useR2RChat';
import useDynamicUrl from '@/composables/useDynamicUrl';
import useOrientation from '@/composables/useOrientation';
import useVisibility from '@/composables/useVisibility';
import useExternalProjects from '@/composables/useExternalProjects';
import usePostHog from '@/composables/usePostHog';
import AppHeader from '@/components/AppHeader.vue';
import AppFooter from '@/components/AppFooter.vue';
import Avatar from '@/components/Avatar.vue';
import LoadingState from '@/components/LoadingState';
import FlowPage from '@/components/FlowPage';
import MenuPage from '@/components/MenuPage';
import StartOverlay from '@/components/StartOverlay';
import OfflineOverlay from '@/components/OfflineOverlay';
import ErrorComponent from '@/components/Error.vue';
import { START_FLOW_ID, SOLITARY_FLOW_ID, ERRORS, SYSTEM_VARIABLES_MAP } from '@/js/constants';
import ACTIONS from '@/actions/types';
import IoTService from '@/js/iot';
import { listenClientApi } from '@/js/client';
import { onSttTextCb } from '@/js/stt';
import AnalyticsService from '@/js/analytics';

export default defineComponent({
  name: 'App',

  components: {
    AppHeader,
    AppFooter,
    Avatar,
    LoadingState,
    FlowPage,
    MenuPage,
    StartOverlay,
    ErrorComponent,
    OfflineOverlay,
  },

  setup() {
    usePostHog();

    const {
      setConfig,
      currentLanguage,
      currentConfig,
      defaultStyles,
      getCurrentSttLanguage,
      setVariablesFromQuery,
      isPreview,
      getVariable,
      setLanguage,
      startNewSession,
      setFlowStateStart,
      setFlowStateBackToStart,
      activeAvatar,
      setActiveAvatar,
      sharedConfig,
    } = useConfig();
    const { setFlows } = useFlows();
    const { processExternalConfig, loadingProjects } = useExternalProjects();
    const {
      setContentData,
      currentContent,
      findContentAssetUrl,
      isVideoMedia,
      isVVideoMedia,
      isHideAvatar,
      isKeepAvatarDesign,
      isIndependentVerticalBg,
      useCenterVerticalDesktopDesign,
      contentPromiseResolve,
      media,
      vMedia,
      hasContent,
    } = useContent();
    const { runSequence, interruptSequence, pauseSequence, resumeSequence, navigateTo } = useSequence();
    const { currentAction } = useActions();
    const { initMQTT, connect, deleteMQTTInstance, mqttSubscribe, isOnline } = useMqtt();
    const { isHumanDetected, initFD, enableFD, disableFD } = useFaceDetection();
    const { initSTT, setSttLanguage, disableStt } = useStt();
    const { isPortrait } = useOrientation();

    const { startListenUserInteraction, unblockSayActions } = useTts();
    const { listenRobotCommand } = useRobotCommand();
    const { init: initAnalytics, setGlobalProps } = useAnalytics();
    const { listenRobotChat, stopChat } = useR2RChat();
    const { avatarState, setWaiting } = useAvatarState();
    const { visibilityHidden } = useVisibility();
    const iotService = new IoTService();

    const isSetupComplete = ref(false);
    const error = ref('');
    const videoRef = ref(null);
    const videoBgRef = ref(null);
    const vVideoBgRef = ref(null);
    const rootRef = ref(null);
    const previewOverlayRef = ref(null);
    const showStartOverlay = ref(true);
    const isComponentProcessing = ref(false);
    const mediaUrl = computed(() => {
      if (media.value?.externalUrl) {
        return useDynamicUrl(media.value.externalUrl);
      }

      if (!media.value?.filename) {
        return null;
      }

      return findContentAssetUrl(media.value.filename);
    });
    const vMediaUrl = computed(() => {
      if (vMedia.value?.externalUrl) {
        return useDynamicUrl(vMedia.value.externalUrl);
      }

      if (!vMedia.value?.filename) {
        return null;
      }

      return findContentAssetUrl(vMedia.value.filename);
    });

    const processingColor = computed(() => defaultStyles.value.headline?.color || '#2a3746');
    const overlayColor = computed(() => defaultStyles.value.overlay);

    const contentStyles = computed(() => {
      const backgroundColor = currentContent.value?.styles?.background_color || defaultStyles.value.backgroundColor || '';
      const mediaBackground = mediaUrl.value && !isVideoMedia.value ? ` url("${mediaUrl.value}") no-repeat center` : '';
      return {
        background: `${backgroundColor}${mediaBackground}`,
      };
    });

    const verticalStyles = computed(() => {
      if (!isIndependentVerticalBg.value) {
        return null;
      }
      const backgroundColor = currentContent.value?.styles?.background_color || defaultStyles.value.backgroundColor || '';
      const mediaBackground = vMediaUrl.value && !isVVideoMedia.value ? ` url("${vMediaUrl.value}") no-repeat center` : '';
      return {
        background: `${backgroundColor}${mediaBackground}`,
      };
    });

    const isHorizontalVideoBg = computed(() => (isIndependentVerticalBg.value ? !isPortrait.value : true));

    const isProcessingAction = computed(() => [
      ACTIONS.API_CALL,
      ACTIONS.CUSTOM_SCRIPT,
      ACTIONS.KNOWLEDGE_BASE,
    ].includes(currentAction.value?.name));

    const avatarReady = ref(false);
    let flowStarted = false;
    let humanLeftTimer = null;
    const ready = computed(() => avatarReady.value);
    const useOverlay = ref(isPreview());
    const disableOverlay = computed(() => currentConfig?.customPayload?.disableOverlay || currentConfig?.disableOverlay || (route?.query?.DisableOverlay?.toLowerCase() === 'true'));

    function onAvatarReady() {
      avatarReady.value = true;
      listenClientApi();
    }

    const avatarLoadingPercentage = ref(0);
    function onAvatarLoadProgress(percents) {
      avatarLoadingPercentage.value = percents;
    }

    function interruptAndRun(id) {
      interruptSequence();
      runSequence(id);
    }

    function postFlowStarted(solitary) {
      flowStarted = !solitary;
      AnalyticsService.setGlobalProperties({
        solitary,
      });
    }

    function runFlowFromElement() {
      navigateTo(route.query.blockId, route.query.elementIndex);
      postFlowStarted(false);
    }

    function runFlowFromStart() {
      interruptAndRun(START_FLOW_ID);
      postFlowStarted(false);
    }

    function checkMainFlow() {
      if (activeAvatar.value !== sharedConfig.value.initialAvatar) {
        // switch back to main avatar
        setActiveAvatar(sharedConfig.value.initialAvatar);
        if (!hasContent(currentLanguage.value)) {
          const newPrimaryLanguage = currentConfig.value.contentLanguages.find((l) => l.isDefault)?.code;
          setLanguage(newPrimaryLanguage);
        }
      }
    }

    function handleNavigateButton(id) {
      checkMainFlow();
      setFlowStateBackToStart(id);
      interruptAndRun(id);
    }

    function goToSolitary() {
      checkMainFlow();
      interruptAndRun(SOLITARY_FLOW_ID);
      postFlowStarted(true);
    }

    function runFlow() {
      stopChat(true);
      if (isHumanDetected.value) {
        runFlowFromStart();
      } else {
        goToSolitary();
      }
    }

    function restartFlow() {
      checkMainFlow();
      runFlow();
    }

    const languageMenuVisible = ref(false);
    function toggleLanguageMenu(visible) {
      if (visible) {
        if (videoBgRef.value) {
          videoBgRef.value.pause();
        }

        if (vVideoBgRef.value) {
          vVideoBgRef.value.pause();
        }
        pauseSequence();
      } else {
        if (videoBgRef.value) {
          videoBgRef.value.play();
        }
        if (vVideoBgRef.value) {
          vVideoBgRef.value.play();
        }
        resumeSequence();
      }
      languageMenuVisible.value = visible;
    }

    async function videoEndedHandler() {
      if (contentPromiseResolve.value) {
        contentPromiseResolve.value();
      }
    }

    async function vVideoEndedHandler() {
      if (contentPromiseResolve.value) {
        contentPromiseResolve.value();
      }
    }

    function startPreviewTimeout() {
      if (route.query.m !== 'preview' && !currentConfig.value.forcePreview) {
        return;
      }

      setTimeout(() => {
        window.location.href = 'https://plural.io/subscribe';
      }, 60 * 1000);
    }

    async function closeStartOverlay() {
      if (!isSetupComplete.value || loadingProjects.value.length > 0) {
        return;
      }

      showStartOverlay.value = false;

      startPreviewTimeout();
      if (route.query.blockId && route.query.elementIndex) {
        runFlowFromElement();
      } else {
        runFlow();
      }

      try {
        await Promise.all([
          iotService.listen(),
          iotService.listenSession(getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value),
          listenRobotCommand(),
          listenRobotChat(),
        ]);
      } catch (err) {
        error.value = ERRORS[err.message] || err.message;
      }
    }

    function updateLanguage(lang) {
      if (activeAvatar.value !== sharedConfig.value.initialAvatar && !hasContent(lang)) {
        setActiveAvatar(sharedConfig.value.initialAvatar);
        setLanguage(lang);
        runFlow();
      } else {
        setLanguage(lang);
      }
    }

    watch(currentLanguage, () => {
      setSttLanguage(getCurrentSttLanguage());
      const language = getVariable(SYSTEM_VARIABLES_MAP.LANGUAGE_ISO2.id).value;
      setGlobalProps({ language });
    });

    function startHumanLeftTimer() {
      if (humanLeftTimer) {
        clearTimeout(humanLeftTimer);
      }
      humanLeftTimer = setTimeout(() => {
        humanLeftTimer = null;
        if (flowStarted) {
          goToSolitary();
        }
      }, currentConfig.value.customPayload.humanLostTimeout || 10000);
    }

    function restartHumanLeftTimer() {
      if (!isHumanDetected.value && humanLeftTimer) {
        clearTimeout(humanLeftTimer);
        startHumanLeftTimer();
      }
    }

    watch(() => [getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id)], () => {
      if (!showStartOverlay.value) {
        iotService.listenSession(getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value);
      }
    }, { deep: true });

    watch(videoBgRef, (video) => {
      if (video) {
        video.loop = currentContent.value.type !== 'elements.MEDIA';
      }
    });
    watch(vVideoBgRef, (vVideo) => {
      if (vVideo) {
        vVideo.loop = currentContent.value.type !== 'elements.MEDIA';
      }
    });

    watch(ready, (isReady) => {
      if (isReady && !flowStarted && isSetupComplete.value
        && (!showStartOverlay.value || currentConfig.value.disableOverlay)) {
        if (currentConfig.value.disableOverlay) {
          setFlowStateStart();
          startNewSession();
          closeStartOverlay();
        } else {
          runFlow();
        }
      }
    });

    watch(isHumanDetected, (detected) => {
      if (!ready.value) {
        return;
      }
      if (detected) {
        clearTimeout(humanLeftTimer);
        humanLeftTimer = null;
        if (!flowStarted && !showStartOverlay.value) {
          setFlowStateStart();
          startNewSession();
          runFlowFromStart();
        }
      } else if (flowStarted) {
        startHumanLeftTimer();
      }
    });

    let initSttPromise = null;
    let initFdPromise = null;

    watch(visibilityHidden, async (isHidden) => {
      if (!ready.value) {
        return;
      }
      if (isHidden) {
        if (currentConfig.value.recognitionServicesActive) {
          if (initFdPromise) {
            await initFdPromise;
          }
          disableFD();
        }
        if (currentConfig.value.speechServicesActive) {
          if (initSttPromise) {
            await initSttPromise;
          }
          disableStt();
        }
        if (!showStartOverlay.value) {
          clearTimeout(humanLeftTimer);
          pauseSequence();
        }
      } else {
        // NOTE: awaiting here causes notable ux delays on reconnecting the stt stocket
        initSttPromise = initSTT(currentConfig.value, onSttTextCb, {
          projectId: currentConfig.value.projectId,
          sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
          isPreview: isPreview(),
        });

        let enabled;
        if (currentConfig.value.recognitionServicesActive) {
          initFdPromise = enableFD();
          enabled = await initFdPromise;
        }

        if (!showStartOverlay.value && !visibilityHidden.value) {
          if (enabled && !isHumanDetected.value) {
            startHumanLeftTimer();
          }
          resumeSequence();
        }
      }
    }, { immediate: true });

    // mute video on background while tts is on
    watch(avatarState, () => {
      if (videoBgRef.value) {
        videoBgRef.value.muted = avatarState.isTalking || null;
      }
      if (vVideoBgRef.value) {
        vVideoBgRef.value.muted = avatarState.isTalking || null;
      }
    });

    watch(isProcessingAction, (value) => {
      setWaiting(value);
    });

    watch(isComponentProcessing, (value) => {
      setWaiting(value);
    });

    function onContentUpdateHandler() {
      window.location.reload();
    }

    function onOutOfCreditsHandler() {
      if (!isPreview() || currentConfig.value.speechServicesActive) {
        window.location.reload();
      }
    }

    async function setupMqtt() {
      initMQTT();
      await connect();
      await mqttSubscribe({ scope: 'robot', topic: 'content/update' }, onContentUpdateHandler);
      await mqttSubscribe({ scope: 'public', topic: 'outofcredits' }, onOutOfCreditsHandler);
    }

    onMounted(async () => {
      try {
        const exportData = await api.getExport('web', route.query.mode);
        if (exportData.error) {
          throw new Error(exportData.error);
        }

        // set currentConfig first
        setConfig(route.getRobotToken(), exportData);
        // set short codes context before setting flows and content data
        ShortCodeService.useContext(useConfig());
        if (disableOverlay.value) {
          showStartOverlay.value = false;
        }
        // then init face detection
        await initFD(exportData.config.recognitionServicesActive ? videoRef.value : null);

        document.title = `${currentConfig.value.projectName} :: ${currentConfig.value.frameName}`;
        setFlows(route.getRobotToken(), exportData);
        startListenUserInteraction();
        setContentData(route.getRobotToken(), exportData, currentLanguage.value);

        initAnalytics();
        setSttLanguage(getCurrentSttLanguage());
        setVariablesFromQuery();
        useOverlay.value = isPreview();
        await setupMqtt();
        await initSTT(currentConfig.value, onSttTextCb, {
          projectId: currentConfig.value.projectId,
          sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
          isPreview: isPreview(),
        });

        // protection from deleting preview-overlay by dev tools
        if (useOverlay.value) {
          const compOverlayStyles = window.getComputedStyle(previewOverlayRef.value);
          const styles = {
            display: compOverlayStyles.getPropertyValue('display'),
            opacity: compOverlayStyles.getPropertyValue('opacity'),
            position: compOverlayStyles.getPropertyValue('position'),
            top: compOverlayStyles.getPropertyValue('top'),
            left: compOverlayStyles.getPropertyValue('left'),
            height: compOverlayStyles.getPropertyValue('height'),
            width: compOverlayStyles.getPropertyValue('width'),
            backgroundImage: compOverlayStyles.getPropertyValue('background-image'),
            backgroundPosition: compOverlayStyles.getPropertyValue('background-position'),
            backgroundRepeat: compOverlayStyles.getPropertyValue('background-repeat'),
            zIndex: compOverlayStyles.getPropertyValue('z-index'),
            pointerEvents: compOverlayStyles.getPropertyValue('pointer-events'),
          };
          setInterval(() => {
            previewOverlayRef.value.classList.add('preview-overlay');
            Object.entries(styles).forEach(([styleName, styleValue]) => {
              previewOverlayRef.value.style[styleName] = styleValue;
            });
            if (!previewOverlayRef.value.parentNode) {
              rootRef.value.appendChild(previewOverlayRef.value);
            }
          }, 10000);
        }

        await processExternalConfig(exportData.config.externalProjects);
        isSetupComplete.value = true;

        if (disableOverlay.value && ready.value) {
          unblockSayActions();
          closeStartOverlay();
        }
      } catch (err) {
        error.value = ERRORS[err.message] || err.message;
      }

      visibilityHidden.value = document.hidden;
    });

    onBeforeUnmount(async () => {
      await deleteMQTTInstance();
    });

    return {
      isOnline,
      isVideoMedia,
      isVVideoMedia,
      isHideAvatar,
      isKeepAvatarDesign,
      isPortrait,
      isHorizontalVideoBg,
      isIndependentVerticalBg,
      isProcessingAction,
      isComponentProcessing,
      isSetupComplete,
      loadingProjects,
      useCenterVerticalDesktopDesign,
      overlayColor,
      mediaUrl,
      vMediaUrl,
      contentStyles,
      verticalStyles,
      ready,
      useOverlay,
      error,
      videoRef,
      videoBgRef,
      vVideoBgRef,
      showStartOverlay,
      currentContent,
      currentAction,
      currentConfig,
      disableOverlay,
      onAvatarReady,
      onAvatarLoadProgress,
      avatarLoadingPercentage,
      languageMenuVisible,
      toggleLanguageMenu,
      runFlow,
      restartFlow,
      closeStartOverlay,
      videoEndedHandler,
      vVideoEndedHandler,
      updateLanguage,
      rootRef,
      previewOverlayRef,
      restartHumanLeftTimer,
      interruptAndRun,
      handleNavigateButton,
      processingColor,
    };
  },
});
</script>

<style lang="scss">
body, html {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
}
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  width: 100%;
  height: 100%;
  background-color: #f8f8f8;
}

* {
  box-sizing: border-box;
}

.processing-action-overlay {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: 1;

  @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
    position: fixed;
  }

  @media screen and (max-height: $mobile_height_breakpoint) {
    position: fixed;
  }

  @media screen and (min-height: $big_vertical_screen_breakpoint) {
    position: absolute;
  }

  &.hide-on-portrait {
    @media screen and (orientation: portrait) {
      display: none;
    }
  }
}

.main {
  height: 100%;
  position: relative;

  @media screen and (orientation: landscape) and (min-width: $desktop_s_breakpoint) {
    display: flex;
    justify-content: space-around;
  }

  @media screen and (max-height: $mobile_height_breakpoint) {
    display: flex;
    justify-content: space-around;
  }

  @media screen and (min-height: $big_vertical_screen_breakpoint) {
    display: block;
  }
}

.horizontal-bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-size: cover !important;

  &.hide-on-portrait {
    @media screen and (orientation: portrait) {
      display: none;
    }
  }
}

.vertical-bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-size: cover !important;

  @media screen and (orientation: landscape) {
    display: none;
  }
}

.webcam {
  position: absolute;
  z-index: -1;
}

#video-background {
  height: 100%;
  width: 100%;
  position: absolute;
  left: 0;
  top: 0;
  object-fit: cover;

  &.hide-on-portrait {
    @media screen and (orientation: portrait) {
      display: none;
    }
  }
}
#video-v-background {
  height: 100%;
  width: 100%;
  position: absolute;
  left: 0;
  top: 0;
  object-fit: cover;

  @media screen and (orientation: landscape) {
    display: none;
  }
}

.preview-overlay {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-image: url('@/assets/preview-overlay.png');
  background-position: center;
  background-repeat: repeat;
  opacity: 0.1;
  z-index: 99999;
  pointer-events: none;
}
 </style>

<style lang="scss" scoped>
.loading-state {
  pointer-events: none;

  &:not(.ready) {
    :deep {
      .loader {
        @media screen and (min-height: $big_vertical_screen_breakpoint) {
          transform: scale(3);
          transform-origin: center center;
          z-index: 6;
        }
      }
    }
  }

  &.ready {
    z-index: 4;

    &.has-avatar {
      :deep {
        .loader {
          position: absolute;
          display: block;
          width: auto;
          height: auto;
          left: calc(50vw + 15%);
          top: calc(50% - 60px);

          // all portraits
          @media screen and (max-width: ($desktop_s_breakpoint - 1px)) {
            display: flex;
            top: unset;
            left: 0;
            bottom: 0;
            width: 100%;
            padding: 20px 0;
            background-color: rgba(0,0,0,0.3);

            svg {
              width: 48px;
              height: 48px;
              stroke: #ffffff !important;
            }

            .loading-text {
              margin-top: 0;
              margin-left: 6px;
              color: #ffffff !important;
              font-size: 30px;
            }
          }

          // phones
          @media screen and (max-width: ($tablet_breakpoint - 1px)) {
            padding: 10px 0;

            svg {
              width: 32px;
              height: 32px;
            }

            .loading-text {
              font-size: 20px;
            }
          }

          @media screen and (min-height: $big_vertical_screen_breakpoint) {
            transform: scale(3);
            transform-origin: bottom;
            width: 100%;
            left: unset;
            top: unset;
          }
        }
      }
    }

    :deep {
      .loader {
        // all portraits
        @media screen and (orientation: portrait) {
          display: flex;
          justify-items: center;
          align-items: center;
          flex-direction: row;
          top: unset;
          position: absolute;
          left: 0;
          bottom: 0;
          width: 100%;
          height: auto;
          padding: 20px 0;
          background-color: rgba(0,0,0,0.3);

          svg {
            width: 48px;
            height: 48px;
            stroke: #ffffff !important;
          }

          .loading-text {
            margin-top: 0;
            margin-left: 6px;
            color: #ffffff !important;
            font-size: 30px;
          }

          // phones
          @media screen and (max-width: ($tablet_breakpoint - 1px)) {
            padding: 10px 0;

            svg {
              width: 32px;
              height: 32px;
            }

            .loading-text {
              font-size: 20px;
            }
          }

          @media screen and (min-height: $big_vertical_screen_breakpoint) {
            transform: scale(3);
            transform-origin: center center;
          }
        }
      }
    }

    &.bottom-navigation-enabled {
      @media screen and (max-width: $desktop_s_breakpoint - 1px) {
        :deep {
          .loader {
            bottom: 64px;
          }
        }
      }
    }

    &.central-design {
      @media screen and (orientation: portrait) and (min-width: $desktop_s_breakpoint - 1px) {
        display: grid;
        justify-content: center;
        align-items: center;
        grid-template-columns: 1fr;

        :deep {
          .loader {
            position: static;
          }
        }
      }
    }
  }
}
</style>
